import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Alert, Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';


import { AI_ENGINE_URL } from '../../utils/apiConfig';

// Style fields to transfer between preset and karaokeStyle
const STYLE_FIELDS = [
  'font_name', 'font_size', 'bold', 'uppercase',
  'text_color', 'text_opacity', 'highlight_color', 'highlight_opacity',
  'bg_color', 'bg_opacity',
  'outline_enabled', 'outline_color', 'outline_opacity', 'outline_width',
  'shadow_enabled', 'shadow_color', 'shadow_opacity', 'shadow_depth',
  'animation_type', 'anim_duration_ms', 'alignment', 'margin_v', 'box_padding'
];

// Map camelCase karaokeStyle keys to snake_case preset fields
const CAMEL_TO_SNAKE = {
  fontName: 'font_name', fontSize: 'font_size',
  textColor: 'text_color', textOpacity: 'text_opacity',
  highlightColor: 'highlight_color', highlightOpacity: 'highlight_opacity',
  bgColor: 'bg_color', bgOpacity: 'bg_opacity',
  outlineEnabled: 'outline_enabled', outlineColor: 'outline_color',
  outlineOpacity: 'outline_opacity', outlineWidth: 'outline_width',
  shadowEnabled: 'shadow_enabled', shadowColor: 'shadow_color',
  shadowOpacity: 'shadow_opacity', shadowDepth: 'shadow_depth',
  animationType: 'animation_type', animDurationMs: 'anim_duration_ms',
  marginV: 'margin_v', boxPadding: 'box_padding',
};
const SNAKE_TO_CAMEL = Object.fromEntries(Object.entries(CAMEL_TO_SNAKE).map(([k, v]) => [v, k]));

function presetToKaraokeStyle(preset) {
  const style = {};
  for (const snakeKey of STYLE_FIELDS) {
    const camelKey = SNAKE_TO_CAMEL[snakeKey] || snakeKey;
    if (preset[snakeKey] !== undefined) {
      style[camelKey] = preset[snakeKey];
    }
  }
  return style;
}

function karaokeStyleToPreset(karaokeStyle) {
  const preset = {};
  for (const [camelKey, snakeKey] of Object.entries(CAMEL_TO_SNAKE)) {
    if (karaokeStyle[camelKey] !== undefined) {
      preset[snakeKey] = karaokeStyle[camelKey];
    }
  }
  // Pass through keys that are same in both
  for (const key of ['bold', 'uppercase', 'alignment']) {
    if (karaokeStyle[key] !== undefined) preset[key] = karaokeStyle[key];
  }
  return preset;
}

function KaraokePresetManager({ open, onClose, karaokeStyle, onLoadPreset }) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const fetchPresets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/karaoke-presets/`);
      if (res.ok) setPresets(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchPresets();
      setError(null);
      setSaveName('');
      setEditingId(null);
    }
  }, [open, fetchPresets]);

  const handleSaveAs = async () => {
    if (!saveName.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const body = { name: saveName.trim(), ...karaokeStyleToPreset(karaokeStyle) };
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/karaoke-presets/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Opslaan mislukt');
      }
      setSaveName('');
      fetchPresets();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (preset) => {
    if (!editName.trim()) return;
    if (!(await bevestig({ titel: 'Preset overschrijven?', tekst: `De opgeslagen stijl van "${preset.name}" wordt vervangen door de huidige stijl.`, knop: 'Overschrijven' }))) return;
    setError(null);
    try {
      const body = { name: editName.trim(), ...karaokeStyleToPreset(karaokeStyle) };
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/karaoke-presets/${preset.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Update mislukt');
      }
      setEditingId(null);
      fetchPresets();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (preset) => {
    if (!(await bevestig({ titel: 'Preset verwijderen?', tekst: `Preset "${preset.name}" wordt definitief verwijderd.`, knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      await authFetch(`${AI_ENGINE_URL}/api/v1/karaoke-presets/${preset.id}`, { method: 'DELETE' });
      fetchPresets();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLoad = (preset) => {
    onLoadPreset(presetToKaraokeStyle(preset));
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Social Subs Presets
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Alert severity="info" sx={{ mb: 2 }}>
          Klik op <strong>Laden</strong> om een preset te activeren. Pas daarna de stijl aan in het panel en sla op als nieuw of overschrijf bestaand.
        </Alert>

        {/* Save current style as preset */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3, alignItems: 'center' }}>
          <TextField
            size="small"
            label="Huidige stijl opslaan als..."
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            sx={{ flex: 1 }}
            onKeyDown={(e) => e.key === 'Enter' && handleSaveAs()}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleSaveAs}
            disabled={!saveName.trim() || saving}
          >
            Opslaan
          </Button>
        </Box>

        {/* Preset list */}
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Naam</TableCell>
                <TableCell>Font</TableCell>
                <TableCell>Kleuren</TableCell>
                <TableCell align="right">Acties</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {presets.map((preset) => (
                <TableRow key={preset.id}>
                  <TableCell>
                    {editingId === preset.id ? (
                      <TextField
                        size="small"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleUpdate(preset)}
                        autoFocus
                        sx={{ minWidth: 120 }}
                      />
                    ) : (
                      <Typography variant="body2" fontWeight="bold">
                        {preset.name}
                        {preset.is_default && <Chip label="Standaard" size="small" sx={{ ml: 1 }} />}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {preset.font_name} {preset.font_size}px
                      {preset.bold && ' Bold'}
                      {preset.uppercase && ' ABC'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: preset.text_color, border: '1px solid #ccc' }} />
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: preset.highlight_color, border: '1px solid #ccc' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        {preset.animation_type}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {editingId === preset.id ? (
                      <>
                        <Button size="small" onClick={() => handleUpdate(preset)}>Opslaan</Button>
                        <Button size="small" onClick={() => setEditingId(null)}>Annuleer</Button>
                      </>
                    ) : (
                      <>
                        <Button size="small" variant="contained" onClick={() => handleLoad(preset)} sx={{ mr: 0.5, minWidth: 'auto', textTransform: 'none' }}>
                          Laden
                        </Button>
                        <Tooltip title="Overschrijf deze preset met je huidige stijl">
                          <IconButton size="small" onClick={() => { setEditingId(preset.id); setEditName(preset.name); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!preset.is_default && (
                          <Tooltip title="Verwijderen">
                            <IconButton size="small" color="error" onClick={() => handleDelete(preset)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {presets.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary">Geen presets gevonden</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Sluiten</Button>
      </DialogActions>
    </Dialog>
  );
}

export default KaraokePresetManager;
